import 'assets/index.css';
import 'twin.macro';
import 'styled-components/macro';

import { ApolloProvider } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { CompatRoute, CompatRouter, Navigate } from 'react-router-dom-v5-compat';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { NotificationProvider } from 'core/context/Notifications';
import { FeatureFlagManager } from 'core/molecules/feature-flag-manager/FeatureFlagManager';
import { SkilljarContextProvider } from 'play/support/skilljar';
import { apolloClient } from 'support/apollo';

import { ScrollToTop } from './component-lib/atoms/ScrollToTop';
import ThemeWrapper from './component-lib/ThemeWrapper';
import { AuthenticationContextProvider, useAuthenticationContext } from './core/context/Authentication';
import { trackAnonUser, trackPageView, trackUser } from './support/tracking';
import { UserSegment } from './types/graphql';

const ArcadeApp = lazy(() => import('./arcade/ArcadeApp'));
const CoreApp = lazy(() => import('./core/CoreApp'));
const PlayApp = lazy(() => import('./play/PlayApp'));
const CreateApp = lazy(() => import('./create/CreateApp'));
const ClassroomApp = lazy(() => import('./classroom/ClassroomApp'));

export function App() {
  return (
    <Providers>
      <ScrollToTop />
      <AppContainer>
        <Router />
        <FeatureFlagManager />
      </AppContainer>
    </Providers>
  );
}

function Providers({ children }: { children: React.ReactNode }) {
  return (
    <BrowserRouter>
      <CompatRouter>
        <ThemeWrapper>
          <ApolloProvider client={apolloClient}>
            <AuthenticationContextProvider>
              <NotificationProvider>
                <SkilljarContextProvider>{children}</SkilljarContextProvider>
              </NotificationProvider>
            </AuthenticationContextProvider>
          </ApolloProvider>
        </ThemeWrapper>
      </CompatRouter>
    </BrowserRouter>
  );
}

function Router() {
  const { instruqtUser } = useAuthenticationContext();

  return (
    <Suspense fallback={<LoadingImage />}>
      <Switch>
        <CompatRoute path="/arcade">
          <ArcadeApp />
        </CompatRoute>

        <Route
          path={[
            '/embed',
            '/embedv2',
            '/:teamSlug/invite/:inviteID/tracks/:trackSlug/challenges',
            '/:teamSlug/invite/:inviteID/tracks/:trackSlug/recap',
            '/:teamSlug/tracks/:trackSlug/challenges',
            '/:teamSlug/tracks/:trackSlug/recap',
          ]}
          component={PlayApp}
        />

        <Route
          exact
          path={[
            '/:teamSlug/tracks/:trackSlug',

            // When moving all inspiration-library to CreateApp we can simply remove these two exact routes
            '/:teamSlug/inspiration-library/:templateSlug/:trackSlug',
          ]}
          component={CoreApp}
        />

        <Route
          path={[
            '/claim-username',
            '/forgot-password',
            '/login',
            '/logout',
            '/not-found',
            '/signup',
            '/study-room',
            '/users',
            '/:teamSlug/invite/:inviteID',
          ]}
          component={CoreApp}
        />

        <Route path="/:teamSlug/invites/:inviteID/live-event" component={ClassroomApp} />

        <Route
          path={[
            '/create-track',
            '/inspiration-library',
            '/:teamSlug/api-keys',
            '/:teamSlug/arcades',
            '/:teamSlug/hot-start',
            '/:teamSlug/inspiration-library',
            '/:teamSlug/invites',
            '/:teamSlug/limits',
            '/:teamSlug/members',
            '/:teamSlug/play-activity-report',
            '/:teamSlug/user-report',
            '/:teamSlug/sandbox-presets',
            '/:teamSlug/sandboxes',
            '/:teamSlug/secrets',
            '/:teamSlug/statistics',
            '/:teamSlug/tracks/:trackSlug/wizard',
            '/:teamSlug/tracks/:trackSlug/editor',
            '/:teamSlug/tracks/:trackSlug/dashboard',
            '/:teamSlug/webhooks',
          ]}
          component={CreateApp}
        />

        <Route
          path="/"
          exact
          render={() =>
            instruqtUser?.segments?.includes(UserSegment.Learner) ? (
              <Navigate replace to="/study-room" />
            ) : (
              <CreateApp />
            )
          }
        />
        <Route
          path="/:teamSlug"
          exact
          render={() =>
            instruqtUser?.segments?.includes(UserSegment.Learner) ? (
              <Navigate replace to="/study-room" />
            ) : (
              <CreateApp />
            )
          }
        />

        <Redirect to="/not-found" />
      </Switch>
    </Suspense>
  );
}

export interface AppContainerProps {
  children?: React.ReactNode;
}

export const AppContainer = ({ children }: AppContainerProps) => {
  const { instruqtUser, isAnonymous } = useAuthenticationContext();
  const location = useLocation();

  // Identify the current user in Sentry / Hubspot
  useEffect(() => {
    if (!isAnonymous) {
      trackUser(instruqtUser);
    } else {
      trackAnonUser();
    }
  }, [instruqtUser, isAnonymous]);

  // Track page views in Firebase / Google Analytics / Hubspot
  useEffect(() => {
    trackPageView(instruqtUser, location);
  }, [instruqtUser, location]);

  return (
    <ErrorBoundary showDialog>
      <div tw="flex flex-col min-h-screen w-full">{children}</div>
    </ErrorBoundary>
  );
};
